import is from '@sindresorhus/is';

import type { JobProfileTaskTransport, ProfileTaskTranslationTransport } from '~/shared/data-contract/profile-task';

import type { WorkTaskUpdateTransport } from '../../../data-contract/profile-task-transport';
import type { ProfileTask, ProfileTaskDraftReference, ProfileTaskSummary } from '../model/profile-task';

export type { JobProfileTaskTransport } from '~/shared/data-contract/profile-task';

export type { ProfileTasksUpdateTransport } from '../../../data-contract/profile-task-transport';

export type ProfileTasksAvailability =
    | { readonly status: 'unavailable' }
    | { readonly status: 'available'; readonly summaries: readonly ProfileTaskSummary[] };

const byOrder = <T extends { readonly order: number }>(left: T, right: T): number => left.order - right.order;

export const mapJobProfileTaskSummaries = (tasks: readonly JobProfileTaskTransport[]): readonly ProfileTaskSummary[] =>
    [...tasks].sort(byOrder).map(({ generationState, taskId, taskVersion, title }) => ({
        generationState,
        taskId,
        taskVersionId: String(taskVersion),
        title: title ?? '',
    }));

export const getProfileTasksAvailability = (
    tasks: readonly JobProfileTaskTransport[] | undefined,
): ProfileTasksAvailability =>
    tasks === undefined
        ? { status: 'unavailable' }
        : { status: 'available', summaries: mapJobProfileTaskSummaries(tasks) };

/**
 * Orders summaries by the task elements the backend synthesizes into the structured content
 * (ref order, titles in the translation's language), joined by taskId. Summaries the elements do
 * not reference keep their transport order at the end; without any joinable element the transport
 * order stands, so responses predating the synthesized elements render unchanged.
 */
export const orderSummariesByStructuredTaskElements = (
    summaries: readonly ProfileTaskSummary[],
    taskElements: readonly { readonly taskId?: string | null; readonly value: string }[],
): readonly ProfileTaskSummary[] => {
    const remaining = new Map(summaries.map((summary) => [summary.taskId, summary]));
    const ordered: ProfileTaskSummary[] = [];
    for (const element of taskElements) {
        const summary = is.nullOrUndefined(element.taskId) ? undefined : remaining.get(element.taskId);
        if (summary === undefined) {
            continue;
        }
        remaining.delete(summary.taskId);
        ordered.push(element.value.trim() === '' ? summary : { ...summary, title: element.value });
    }

    return ordered.length === 0 ? summaries : [...ordered, ...remaining.values()];
};

const ENGLISH_LANGUAGE = 'en-US';

const pickTranslation = <Translation extends { readonly language: string }>(
    translations: readonly Translation[] | undefined,
    language: string | undefined,
    originLanguage: string | undefined,
): Translation | undefined => {
    if (language === undefined || translations === undefined || translations.length === 0) {
        return undefined;
    }

    return (
        translations.find((translation) => translation.language === language) ??
        translations.find((translation) => translation.language === originLanguage) ??
        translations.find((translation) => translation.language === ENGLISH_LANGUAGE) ??
        translations[0]
    );
};

/**
 * Picks the task body for the requested language through the standard fallback chain:
 * requested → origin → English → first available. Responses without translations (older backends)
 * resolve to nothing, so the top-level fields — already resolved by the backend — stand.
 */
export const pickProfileTaskTranslation = (
    task: JobProfileTaskTransport,
    language: string | undefined,
): ProfileTaskTranslationTransport | undefined =>
    pickTranslation(task.translations, language, task.translationOriginLanguage);

export const mapJobProfileTaskDetail = (task: JobProfileTaskTransport, language?: string): ProfileTask => {
    const translation = pickProfileTaskTranslation(task, language);
    // A picked entry replaces the localizable body wholesale; a partially applied entry would mix
    // languages. Canonical values (classification value, hours, skills) stay language-independent.
    const workflow = translation === undefined ? task.workflow : translation.workflow;

    return {
        classification: task.classification?.value,
        classificationReasoning:
            task.classification === undefined
                ? undefined
                : translation === undefined
                  ? task.classification.reasoning
                  : translation.classificationReasoning,
        clusters:
            task.clusters?.map((cluster) => ({
                id: cluster.id,
                name:
                    pickTranslation(cluster.translations, language, cluster.translationOriginLanguage)?.name ??
                    cluster.name,
            })) ?? [],
        description: translation === undefined ? task.description : translation.description,
        effort:
            task.estimatedEffort === undefined
                ? undefined
                : {
                      maximumHoursPerWeek: task.estimatedEffort.maximumHoursPerWeek,
                      minimumHoursPerWeek: task.estimatedEffort.minimumHoursPerWeek,
                  },
        effortReasoning:
            task.estimatedEffort === undefined
                ? undefined
                : translation === undefined
                  ? task.estimatedEffort.reasoning
                  : translation.estimatedEffortReasoning,
        generationState: task.generationState,
        requiredSkills: [...task.requiredSkills].sort(byOrder).map(({ id, level, skillType, title }) => ({
            level,
            skillId: id,
            title,
            type: skillType,
        })),
        taskId: task.taskId,
        taskVersionId: String(task.taskVersion),
        title: translation?.title ?? task.title ?? '',
        workflowSteps:
            workflow?.steps.map(({ description, title }, index) => ({
                description,
                title,
                workflowStepId: `step-${index}`,
            })) ?? [],
    };
};

/** Template preview tasks are not persisted, so they are keyed by their position in the TASKS section instead of a taskId. */
export const mapJobProfileTaskPreviews = (
    tasks: readonly JobProfileTaskTransport[] | undefined,
    language: string | undefined,
): ReadonlyMap<number, ProfileTask> =>
    new Map((tasks ?? []).map((task) => [task.order, mapJobProfileTaskDetail(task, language)]));

/** The backend rejects blank reasonings while manual picks intentionally have none. */
const MANUALLY_EDITED_REASONING = 'Manually edited by User';

const reasoningOrManualFallback = (reasoning: string | undefined): string => {
    const trimmed = reasoning?.trim();

    return trimmed === undefined || trimmed.length === 0 ? MANUALLY_EDITED_REASONING : trimmed;
};

/** `taskVersionId` is the stringified task version (see `mapJobProfileTaskSummaries`); unsaved tasks carry none. */
const parseSourceTaskVersion = (sourceTaskVersionId: string | undefined): number | undefined => {
    const version = Number(sourceTaskVersionId);

    return Number.isInteger(version) && version > 0 ? version : undefined;
};

export const serializeProfileTaskDraftReference = (reference: ProfileTaskDraftReference): WorkTaskUpdateTransport => {
    const { draft, sourceTaskVersionId, taskId } = reference;
    const version = parseSourceTaskVersion(sourceTaskVersionId);
    if (draft === undefined) {
        return version === undefined ? { taskId } : { taskId, version };
    }

    return {
        taskId,
        ...(version === undefined ? {} : { version }),
        title: draft.title,
        description: draft.description ?? '',
        requiredSkills: [...draft.requiredSkillIds],
        workflow:
            draft.workflowSteps.length === 0
                ? null
                : {
                      steps: draft.workflowSteps.map(({ description, title }) => ({
                          description: description ?? '',
                          title,
                      })),
                  },
        // The backend keeps omitted fields untouched, so cleared values must go out as explicit null.
        classification:
            draft.classification === undefined
                ? null
                : {
                      reasoning: reasoningOrManualFallback(draft.classificationReasoning),
                      value: draft.classification,
                  },
        estimatedEffort:
            draft.effort?.maximumHoursPerWeek === undefined || draft.effort.minimumHoursPerWeek === undefined
                ? null
                : {
                      maximumHoursPerWeek: draft.effort.maximumHoursPerWeek,
                      minimumHoursPerWeek: draft.effort.minimumHoursPerWeek,
                      reasoning: reasoningOrManualFallback(draft.effortReasoning),
                  },
    };
};

export class ProfileTaskNotFoundError extends Error {
    constructor(taskId: string) {
        super(`Task ${taskId} is not part of the parent's tasks section`);
        this.name = 'ProfileTaskNotFoundError';
    }
}
